import React, { useState, useEffect } from "react";
import { CheckCircle2, MessageSquare, Save, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const SMSSettingsPage: React.FC = () => {
  const [sms, setSms] = useState({
    provider: "Twilio SMS Gateway",
    enabled: false,
    sender_id: "CORVEN",
    otp_expiry_minutes: 5,
    daily_limit: 10000,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("sms");
      if (res.data) {
        setSms((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load SMS gateway settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("sms", sms);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save SMS settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading SMS gateway settings...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">SMS Gateway Configuration</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Gateway Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Gateway</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">SMS Gateway Provider</label>
          <select
            value={sms.provider}
            onChange={(e) => {
              setSaved(false);
              setSms({ ...sms, provider: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          >
            <option value="Twilio SMS Gateway">Twilio SMS Gateway</option>
            <option value="MSG91 India">MSG91 India DLT Gateway</option>
            <option value="AWS SNS SMS">AWS SNS SMS Service</option>
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">DLT Sender ID / Header</label>
          <input
            type="text"
            value={sms.sender_id}
            onChange={(e) => {
              setSaved(false);
              setSms({ ...sms, sender_id: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">OTP Expiry (Minutes)</label>
          <input
            type="number"
            value={sms.otp_expiry_minutes}
            onChange={(e) => {
              setSaved(false);
              setSms({ ...sms, otp_expiry_minutes: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono"
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-xl font-mono">
          <div>
            <span className="font-bold text-slate-200 font-sans block">Enable SMS Dispatch</span>
            <span className="text-[11px] text-slate-400 font-sans">Active SMS alerts for transactional & OTP messages.</span>
          </div>
          <input
            type="checkbox"
            checked={sms.enabled}
            onChange={(e) => {
              setSaved(false);
              setSms({ ...sms, enabled: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
